import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { Dice5 } from "lucide-react";
import { toast } from "sonner";

export default function Dice() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [target, setTarget] = useState(() => Number(searchParams.get("target")) || 50);
  const [direction, setDirection] = useState(() => searchParams.get("direction") || "over");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const winChance = direction === "over" ? (100 - target) : target;
  const fairMult = 100 / winChance;
  const expectedMult = (fairMult * 0.97).toFixed(4);

  const roll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/instant/dice/roll", {
        bet: Number(bet), currency, target: Number(target), direction,
      });
      setResult(r.data);
      refresh();
      if (r.data.win) toast.success(`Rolled ${r.data.roll} · Won ${fmt(r.data.payout, currency)}`);
      else toast.error(`Rolled ${r.data.roll} · Lost`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const rollColor = result ? (result.win ? "text-emerald-400" : "text-red-500") : "text-[#d4af37]";
  const percent = result ? result.roll : 50;

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Dice5 className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Dice</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">Roll 0.00 – 99.99 · Over or Under</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="lux-card p-8">
          <div className="text-center mb-8">
            <div className={`font-serif text-8xl ${rollColor}`}>
              {result ? result.roll.toFixed(2) : "—"}
            </div>
            {result && <div className={`mt-2 uppercase tracking-widest text-sm ${result.win ? "text-emerald-400" : "text-red-500"}`}>
              {result.win ? `WIN · +${fmt(result.payout, currency)}` : "LOSE"}
            </div>}
          </div>

          {/* Slider visualization */}
          <div className="relative h-8 bg-[#1a1a1a] rounded-full mb-4 overflow-hidden">
            <div className={`absolute top-0 h-full ${direction === "over" ? "right-0 bg-emerald-900/40" : "left-0 bg-emerald-900/40"}`}
                 style={{ width: `${direction === "over" ? (100 - target) : target}%` }}/>
            <div className={`absolute top-0 h-full ${direction === "over" ? "left-0 bg-red-900/40" : "right-0 bg-red-900/40"}`}
                 style={{ width: `${direction === "over" ? target : (100 - target)}%` }}/>
            {result && (
              <div className="absolute top-0 h-full w-1 bg-white transition-all duration-500" style={{ left: `${percent}%` }}/>
            )}
          </div>

          <input data-testid="dice-target" type="range" min={2} max={98} step={0.5} value={target}
                 onChange={e=>setTarget(Number(e.target.value))} className="w-full"/>
          <div className="flex justify-between text-xs text-white/50 mt-2">
            <span>0</span>
            <span className="text-[#d4af37] font-bold">Target: {target}</span>
            <span>99.99</span>
          </div>

          <div className="flex gap-2 mt-6 justify-center">
            <button data-testid="dice-under" onClick={()=>setDirection("under")}
                    className={`px-6 py-2 rounded ${direction === "under" ? "btn-gold" : "btn-ghost"}`}>
              Roll Under
            </button>
            <button data-testid="dice-over" onClick={()=>setDirection("over")}
                    className={`px-6 py-2 rounded ${direction === "over" ? "btn-gold" : "btn-ghost"}`}>
              Roll Over
            </button>
          </div>
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="dice-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded bg-black/40 p-3">
              <div className="text-[10px] text-white/40 uppercase">Chance</div>
              <div className="text-[#d4af37] font-bold">{winChance.toFixed(2)}%</div>
            </div>
            <div className="rounded bg-black/40 p-3">
              <div className="text-[10px] text-white/40 uppercase">Payout</div>
              <div className="text-[#d4af37] font-bold">{expectedMult}x</div>
            </div>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          <button data-testid="dice-roll" onClick={roll} disabled={busy}
                  className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
            🎲 Roll
          </button>
        </div>
      </div>
    </div>
  );
}
